import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { currencies } from '@/utils/currencies';
import { timezones } from '@/utils/timezones';

export default function SetupOrganization() {
  const [orgName, setOrgName] = useState('');
  const [currency, setCurrency] = useState('INR');
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const { createOrganization, user, activeOrg, organizations, loading } = useAuth();
  const navigate = useNavigate();

  // Redirect if user already has an organization or user doesn't exist
  // Only check on initial mount, not during creation process
  useEffect(() => {
    // Skip check if we're in the process of creating an organization
    if (isCreating) return;
    
    if (activeOrg || (organizations && organizations.length > 0)) {
      toast.info('You already have an organization');
      navigate('/', { replace: true });
    }
    
    // If user is null after loading, redirect to login (stale token)
    if (!user && !loading) {
      console.log('No user found in SetupOrganization - redirecting to login');
      toast.error('Please login to continue');
      navigate('/login', { replace: true });
    }
  }, [activeOrg, organizations, user, loading, navigate, isCreating]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    setIsCreating(true); // Prevent useEffect from triggering during creation

    try {
      await createOrganization(orgName, currency, timezone);
      toast.success('Organization created successfully!');
      // Navigation is handled in AuthContext
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
      setIsCreating(false); // Reset flag on error
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center gradient-soft p-4">
      <Card className="w-full max-w-2xl p-8 md:p-12 shadow-strong">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full gradient-primary mb-4">
            <Building2 className="h-8 w-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            Welcome, {user?.name}! <Sparkles className="inline h-6 w-6 text-gold" />
          </h1>
          <p className="text-lg text-muted-foreground mb-4">
            Let's set up your wedding planning organization
          </p>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Your organization is where you'll manage all your wedding projects, team members, and clients.
          </p>
        </div>

        {error && (
          <Alert className="mb-6 border-destructive bg-destructive/10">
            <AlertDescription className="text-destructive">{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="orgName" className="text-base">Organization Name</Label>
            <p className="text-sm text-muted-foreground mb-2">
              This could be your company name, your name, or any identifier you prefer
            </p>
            <div className="relative mt-2">
              <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                id="orgName"
                type="text"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="e.g., Dream Weddings Co, Priya's Events"
                className="pl-11 h-12 text-base"
                required
                disabled={submitting}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="currency" className="text-base">Currency</Label>
            <p className="text-sm text-muted-foreground mb-2">
              Select your primary currency for budgets and payments
            </p>
            <Select value={currency} onValueChange={setCurrency} disabled={submitting}>
              <SelectTrigger className="h-12 text-base">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {currencies.map((curr) => (
                  <SelectItem key={curr.code} value={curr.code}>
                    {curr.symbol} - {curr.name} ({curr.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="timezone" className="text-base">Timezone</Label>
            <p className="text-sm text-muted-foreground mb-2">
              Choose your timezone for event scheduling
            </p>
            <Select value={timezone} onValueChange={setTimezone} disabled={submitting}>
              <SelectTrigger className="h-12 text-base">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timezones.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium">What happens next?</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">✓</span>
                <span>You'll become the organization admin with full access</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">✓</span>
                <span>You can invite team members and assign roles</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">✓</span>
                <span>Start creating and managing wedding projects</span>
              </li>
            </ul>
          </div>

          <Button type="submit" className="w-full gradient-primary h-12 text-base" disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Creating organization...
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5" />
                Create Organization
              </>
            )}
          </Button>
        </form>
      </Card>
    </div>
  );
}
